import React, { useState, useLayoutEffect } from 'react';
import { isNull, max } from 'lodash';

import Legend from '@magoosh/lib/charts/legend';

interface Props {
  colorPalette: {
    dark: string;
    light: string;
  };
  data: {
    answerCount?: number;
    label: string;
    percentage?: boolean;
    range?: {
      high: number;
      low: number;
    };
    value: number;
  }[];
  hideLabel?: boolean;
  labelWidth?: number;
}

const SimpleBarChart: React.FC<Props> = (props) => {
  const [labelWidth, setLabelWidth] = useState(0);

  useLayoutEffect(() => {
    if (props.hideLabel) return;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = "14px 'Tondo', Arial, 'Helvetica Neue', Helvetica, sans-serif";

    const labels = props.data.map((series) => series.label);
    const width = max(labels.map((label) => context.measureText(label).width));

    setLabelWidth(width);
  }, [props.data]);

  return (
    <div className="d-flex flex-column gap-m">
      <div className="d-flex flex-column gap-s">
        {
          props.data.map((series, idx) => (
            <div key={idx} className="align-items-center d-flex gap-s">
              {
                !props.hideLabel && series.label && (
                  <div className="small text-right" style={{ width: props.labelWidth || labelWidth }}>
                    {series.label}
                  </div>
                )
              }

              <div className="flex-1">
                <SectionedBar
                  colorPalette={props.colorPalette}
                  {...series}
                />
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
};

interface SectionedBarProps {
  colorPalette: {
    dark: string;
    light: string;
  };
  answerCount?: number;
  label: string;
  percentage?: boolean;
  range?: {
      high: number;
      low: number;
    };
  value: number;
}

const SectionedBar: React.FC<SectionedBarProps> = (props) => {
  const noGrade = () => {
    return isNull(props.value);
  };

  const noActivity = () => {
    return !props.answerCount || props.answerCount === 0;
  };

  const renderLabel = () => {
    if (noActivity()) {
      return 'No activity';
    } else if (noGrade()) {
      return 'No grade';
    } else if (props.percentage) {
      return `${props.value}%`;
    } else if (props.range) {
      return `${props.value}/${props.range.high}`;
    } else {
      return props.value;
    }
  };

  const width = () => {
    const maximum = props.range?.high || 100;

    return (props.value / maximum) * 100;
  }

  return (
    <div style={{ position: 'relative' }}>
      {
        noActivity() || noGrade() ? (
          <div
            style={{
              backgroundColor: props.colorPalette.light,
              borderRadius: 8,
              height: 20,
              width: '100%'
            }}
          />
        ) : (
          <div className="d-flex">
            <div
              style={{
                backgroundColor: props.colorPalette.dark,
                borderRadius: '9px 0 0 9px',
                height: 18,
                width: 35
              }}
            />

            <div className="d-flex flex-1 gap-xxs">
              <div
                style={{
                  backgroundColor: props.colorPalette.dark,
                  borderRadius: props.value === 100 ? '0 9px 9px 0' : 0,
                  height: 18,
                  width: `${width()}%`
                }}
              />

              <div
                style={{
                  backgroundColor: props.colorPalette.light,
                  borderRadius: '0 9px 9px 0',
                  height: 18,
                  width: `${100 - width()}%`
                }}
              />
            </div>
          </div>
        )
      }

      <div
        style={{
          color: noActivity() || noGrade() ? props.colorPalette.dark : '#ffffff',
          fontSize: 12,
          fontWeight: 'bold',
          left: 10,
          top: '50%',
          position: 'absolute',
          transform: 'translateY(-50%)',
          zIndex: 10
        }}
      >
        {renderLabel()}
      </div>
    </div>
  );
};

export default SimpleBarChart;
